import { useEffect } from 'react';
import type { RefRegistry } from '../ref-registry';
import type { TimeWindow } from '../utils';
import { ModalFooter } from '../../modal/layout/ModalFooter';
import { ModalHeader } from '../../modal/layout/ModalHeader';
import { LdenAlarmForm } from './components/LdenAlarmForm';
import { useFloatingModal } from './hooks/useFloatingModal';
import { useModalForm } from './hooks/useModalForm';
import { useModalInteractions } from './hooks/useModalInteractions';
import type { ModalFormData } from './types';

interface ModalFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (ldenSchedule: ModalFormData, blockData: TimeWindow) => void;
    onDelete?: (data: ModalFormData) => void;
    data: ModalFormData;
    customTemplate?: TimeWindow[] | null;
    refRegistry: RefRegistry;
    dayStartOffset: number;
}

function ModalForm({
    isOpen,
    onClose,
    onSave,
    onDelete,
    data,
    customTemplate,
    refRegistry,
    dayStartOffset,
}: ModalFormProps) {
    const {
        formData,
        errors,
        handleChange,
        handleDayChange,
        handleSave,
        handleDeleteSchedule,
        alarmSettings,
    } = useModalForm({
        ldenSchedule: data,
        customTemplate,
        onSave,
        onClose,
        dayStartOffset,
    });

    const { refs, floatingStyles } = useFloatingModal(isOpen);

    useEffect(
        () => refs.setReference(refRegistry.findRefByTimeWindow(data)),
        [data, refRegistry, refs]
    );

    useModalInteractions({ isOpen, onClose, modalRef: refs.floating });

    function handleDelete() {
        if (onDelete && data) {
            handleDeleteSchedule(data);
            onDelete(data);
            onClose();
        }
    }

    return (
        <div className="z-modal pointer-events-auto fixed inset-0 duration-200">
            <div
                ref={refs.setFloating}
                data-testid="edit-block-modal-form"
                className="flex max-h-screen max-w-md flex-col rounded-lg bg-white py-6 shadow-lg"
                style={floatingStyles}
            >
                <ModalHeader onClose={onClose} title={formData.name || gettext('EDIT')} />
                <LdenAlarmForm
                    formData={formData}
                    errors={errors}
                    handleChange={handleChange}
                    handleDayChange={handleDayChange}
                    alarmSettings={alarmSettings}
                />
                <ModalFooter
                    onClose={onClose}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    saveLabel={gettext('UPDATE')}
                />
            </div>
        </div>
    );
}

export default ModalForm;
